import { useState, type FormEvent, type MouseEvent } from 'react';

import { Button, Card, cn, Field, focusRing, Input } from '@/design-system';
import { formatIban, type CompanyDetails, type Tenant } from '@/entities/tenant';
import { errorMessage, isApiError } from '@/shared/api';
import { AddressLookupFields, useAddressLookup, type AddressFieldName } from '@/shared/ui';

import { companyAddressCopy, companyCopy as copy, settingsCopy } from '../copy';
import { useUpdateCompanyDetails } from '../hooks';
import { NHG_REGISTERED_AS_ID } from './NhgSettingsCard';

const SMALL = 'text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]';

export interface CompanyDetailsCardProps {
    tenant: Tenant;
    /** Wijzigen vraagt `settings.manage`; zonder dat recht staan de gegevens er alleen om te lezen. */
    canManage: boolean;
}

type TextField = Exclude<keyof CompanyDetails, 'address'>;
type TextValues = Pick<CompanyDetails, TextField>;

/** De velden van het formulier onder de namen die de API in een 422 gebruikt. */
const API_FIELDS: Record<TextField | AddressFieldName, string> = {
    name: 'name',
    legalName: 'legal_name',
    kvkNumber: 'kvk_number',
    vatNumber: 'vat_number',
    iban: 'iban',
    email: 'email',
    phone: 'phone',
    website: 'website',
    street: 'address_street',
    houseNumber: 'address_number',
    suffix: 'address_suffix',
    postcode: 'address_postcode',
    city: 'address_city',
};

function valuesOf(tenant: Tenant): TextValues {
    return {
        name: tenant.name,
        legalName: tenant.legalName ?? '',
        kvkNumber: tenant.kvkNumber ?? '',
        vatNumber: tenant.vatNumber ?? '',
        iban: formatIban(tenant.iban),
        email: tenant.email ?? '',
        phone: tenant.phone ?? '',
        website: tenant.website ?? '',
    };
}

/**
 * Bedrijfsgegevens (`PATCH /tenant`): wat klanten, rapporten en facturen over het
 * bedrijf laten zien.
 *
 * Onder elk veld staat in één regel waar het terechtkomt, en of het in de
 * NHG-bijlage of in de berichten aan klanten staat. Het adres zoekt op in de BAG,
 * zoals het adres van een klant ({@link AddressLookupFields}). Opslaan bewaart alles
 * tegelijk; de API schrijft KvK-nummer, btw-nummer, website en adres in de vorm
 * waarin ze gedrukt worden, en het formulier neemt die vorm daarna over. Het IBAN
 * bewaart de API zonder spaties; het formulier toont het in groepjes van vier, zoals
 * op de factuur.
 *
 * "Ingeschreven als" hoort ook bij de keuringsinstantie in de NHG-bijlage, maar staat
 * bij de kaart NHG-bijlage; hier staat een link die daarheen gaat.
 */
export function CompanyDetailsCard({ tenant, canManage }: CompanyDetailsCardProps) {
    return (
        <Card padding="lg" as="section" aria-labelledby="company-details-title">
            <h2
                id="company-details-title"
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {copy.title}
            </h2>
            <p className={`m-0 mt-1 mb-3 ${SMALL}`}>{copy.intro}</p>

            {canManage ? (
                <CompanyDetailsForm tenant={tenant} />
            ) : (
                <CompanyDetailsList tenant={tenant} />
            )}
        </Card>
    );
}

function CompanyDetailsForm({ tenant }: { tenant: Tenant }) {
    const save = useUpdateCompanyDetails();
    const [values, setValues] = useState<TextValues>(() => valuesOf(tenant));
    const address = useAddressLookup(tenant.address);
    const [nameMissing, setNameMissing] = useState(false);
    const [addressIncomplete, setAddressIncomplete] = useState(false);
    const [savedNotice, setSavedNotice] = useState(false);

    const fieldError = (field: TextField | AddressFieldName): string | undefined => {
        if (field === 'name' && nameMissing) {
            return copy.nameMissing;
        }

        return isApiError(save.error)
            ? save.error.firstValidationError(API_FIELDS[field])
            : undefined;
    };

    const set = (field: TextField) => (event: { target: { value: string } }) => {
        const value = event.target.value;

        setSavedNotice(false);
        setValues((current) => ({ ...current, [field]: value }));

        if (field === 'name') {
            setNameMissing(false);
        }
    };

    function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        const resolved = address.resolved;

        setSavedNotice(false);
        setNameMissing(values.name.trim() === '');
        setAddressIncomplete(resolved === null);

        if (values.name.trim() === '' || resolved === null) {
            return;
        }

        save.mutate(
            { ...values, address: resolved },
            {
                onSuccess: (saved) => {
                    setValues(valuesOf(saved));
                    address.load(saved.address);
                    setSavedNotice(true);
                },
            },
        );
    }

    const generalProblem =
        save.error && !(isApiError(save.error) && save.error.kind === 'validation')
            ? errorMessage(save.error)
            : null;

    return (
        <form onSubmit={submit} noValidate className="grid gap-[10px] sm:grid-cols-2">
            <Field
                label={copy.name}
                description={copy.nameHint}
                error={fieldError('name')}
                className="sm:col-span-2"
            >
                <Input value={values.name} autoComplete="organization" onChange={set('name')} />
            </Field>

            <Field
                label={copy.legalName}
                description={copy.legalNameHint}
                error={fieldError('legalName')}
                className="sm:col-span-2"
            >
                <Input value={values.legalName} autoComplete="off" onChange={set('legalName')} />
            </Field>

            <fieldset className="m-0 grid min-w-0 gap-3 border-0 p-0 sm:col-span-2">
                <legend className="float-left mb-[2px] w-full p-0 font-[family-name:var(--font-body)] text-[12px] leading-[1.3] text-[var(--text-secondary)]">
                    {copy.address}
                </legend>
                <p className={`m-0 ${SMALL}`}>{copy.addressHint}</p>
                <AddressLookupFields
                    address={address}
                    copy={companyAddressCopy}
                    allowManualEntry
                    errors={{
                        postcode: fieldError('postcode'),
                        houseNumber: fieldError('houseNumber'),
                        suffix: fieldError('suffix'),
                        street: fieldError('street'),
                        city: fieldError('city'),
                    }}
                />
                {addressIncomplete && address.resolved === null ? (
                    <p role="alert" className="m-0 text-[12px] leading-[1.4] text-[var(--danger)]">
                        {copy.addressIncomplete}
                    </p>
                ) : null}
            </fieldset>

            <Field label={copy.phone} description={copy.phoneHint} error={fieldError('phone')}>
                <Input
                    type="tel"
                    inputMode="tel"
                    value={values.phone}
                    autoComplete="tel"
                    onChange={set('phone')}
                />
            </Field>

            <Field label={copy.email} description={copy.emailHint} error={fieldError('email')}>
                <Input
                    type="email"
                    inputMode="email"
                    value={values.email}
                    autoComplete="email"
                    spellCheck={false}
                    onChange={set('email')}
                />
            </Field>

            <Field
                label={copy.website}
                description={copy.websiteHint}
                error={fieldError('website')}
                className="sm:col-span-2"
            >
                <Input
                    type="url"
                    inputMode="url"
                    value={values.website}
                    autoComplete="url"
                    spellCheck={false}
                    onChange={set('website')}
                />
            </Field>

            <Field label={copy.kvk} description={copy.kvkHint} error={fieldError('kvkNumber')}>
                <Input
                    mono
                    inputMode="numeric"
                    value={values.kvkNumber}
                    autoComplete="off"
                    onChange={set('kvkNumber')}
                />
            </Field>

            <Field label={copy.vat} description={copy.vatHint} error={fieldError('vatNumber')}>
                <Input
                    mono
                    value={values.vatNumber}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={set('vatNumber')}
                />
            </Field>

            <Field label={copy.iban} description={copy.ibanHint} error={fieldError('iban')}>
                <Input
                    mono
                    value={values.iban}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={set('iban')}
                />
            </Field>

            <RegisteredAsNote />

            <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
                <Button type="submit" variant="primary" size="md" disabled={save.isPending}>
                    {save.isPending ? copy.saving : copy.save}
                </Button>
                {savedNotice && !save.isPending ? (
                    <span
                        role="status"
                        className="text-[12px] leading-[1.4] text-[var(--accent-dark)]"
                    >
                        {copy.saved}
                    </span>
                ) : null}
            </div>

            {generalProblem ? (
                <p
                    role="alert"
                    className="m-0 text-[12px] leading-[1.4] text-[var(--danger)] sm:col-span-2"
                >
                    {generalProblem}
                </p>
            ) : null}
        </form>
    );
}

/** Wat iemand zonder `settings.manage` ziet: de gegevens, om te lezen. */
function CompanyDetailsList({ tenant }: { tenant: Tenant }) {
    const street = [
        tenant.address.street,
        `${tenant.address.number ?? ''}${tenant.address.suffix ?? ''}`,
    ]
        .filter((part) => part !== null && part.trim() !== '')
        .join(' ');
    const place = [tenant.address.postcode, tenant.address.city]
        .filter((part) => part !== null && part.trim() !== '')
        .join(' ');
    const rows: Array<[string, string | null]> = [
        [copy.name, tenant.name],
        [copy.legalName, tenant.legalName],
        [copy.address, [street, place].filter((line) => line !== '').join(', ') || null],
        [copy.phone, tenant.phone],
        [copy.email, tenant.email],
        [copy.website, tenant.website],
        [copy.kvk, tenant.kvkNumber],
        [copy.vat, tenant.vatNumber],
        [copy.iban, tenant.iban === null ? null : formatIban(tenant.iban)],
    ];

    return (
        <>
            <dl className="m-0 grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-x-3 gap-y-[6px] text-[13px] leading-[1.45]">
                {rows.map(([label, value]) => (
                    <div key={label} className="contents">
                        <dt className="text-[var(--text-tertiary-safe)]">{label}</dt>
                        <dd
                            className={
                                value
                                    ? 'm-0 break-words text-[var(--text-primary)]'
                                    : 'm-0 text-[var(--text-tertiary-safe)]'
                            }
                        >
                            {value ?? copy.empty}
                        </dd>
                    </div>
                ))}
            </dl>
            <p className={`m-0 mt-3 ${SMALL}`}>{settingsCopy.readOnly}</p>
        </>
    );
}

/**
 * "Ingeschreven als" staat bij de NHG-bijlage; de link gaat naar die keuzelijst en zet
 * de focus erop, zodat je met het toetsenbord meteen verder kunt.
 */
function RegisteredAsNote() {
    function goToRegisteredAs(event: MouseEvent<HTMLAnchorElement>) {
        const target = document.getElementById(NHG_REGISTERED_AS_ID);

        if (!target) {
            return;
        }

        event.preventDefault();
        target.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        target.querySelector<HTMLElement>('button, select, input')?.focus({ preventScroll: true });
    }

    return (
        <p className={`m-0 sm:col-span-2 ${SMALL}`}>
            {copy.registeredAsBefore}
            <a
                href={`#${NHG_REGISTERED_AS_ID}`}
                onClick={goToRegisteredAs}
                className={cn(
                    'rounded-[2px] text-[var(--accent)] underline-offset-2 hover:text-[var(--accent-dark)] hover:underline',
                    focusRing,
                )}
            >
                {copy.registeredAsLink}
            </a>
            {copy.registeredAsAfter}
        </p>
    );
}
